import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from "react-helmet";
import { useParams, useNavigate } from 'react-router-dom';
import '../../styles/Triggers.css';
import RobotSelection from '../Common/RobotSelection';
import FolderSelection from '../Common/FolderSelection';
import axios from 'axios';
import config from '../../config/config';
import ProcessSelection from '../Common/ProcessSelection';
import { toast } from 'react-toastify';
import { useTenant } from '../../context/TenantContext';
import LoggingService from '../../services/LoggingService';

const EditFilePathTrigger = () => {
    let { id } = useParams();
    const { selectedTenant } = useTenant();
    const navigate = useNavigate();

    // Group all state declarations together
    const [folderPath, setFolderPath] = useState('');
    const [fileFormat, setFileFormat] = useState('');
    const [process, setProcess] = useState({ id: '', dbId: '', name: '' });
    const [robot, setRobot] = useState('All');
    const [name, setName] = useState('');
    const [folder, setFolder] = useState('');
    const [status, setStatus] = useState('');
    const [argumentsList, setArgumentsList] = useState([]);
    const [folderPathError, setFolderPathError] = useState('');
    const [fileFormatError, setFileFormatError] = useState('');
    const [priority, setPriority] = useState('Inherited');
    const [stickToRobot, setStickToRobot] = useState(false);

    // Add this function definition
    const handleStickToRobotChange = (e) => {
        const newValue = e.target.checked;
        console.log("🔄 Checkbox changed to:", newValue);
        setStickToRobot(newValue);
    };

    const priorities = [
        { value: "Inherited", label: "Inherited", icon: "○" },
        { value: "Critical", label: "Critical", icon: "◊" },
        { value: "Highest", label: "Highest", icon: "≫" },
        { value: "Very high", label: "Very high", icon: "≫" },
        { value: "High", label: "High", icon: "∧" },
        { value: "Medium-high", label: "Medium-high", icon: "⋀" },
        { value: "Medium", label: "Medium", icon: "=" },
        { value: "Medium-low", label: "Medium-low", icon: "⋁" },
        { value: "Low", label: "Low", icon: "∨" },
        { value: "Very low", label: "Very low", icon: "≪" },
        { value: "Lowest", label: "Lowest", icon: "≪" }
    ];

    const validateFolderPath = (path) => {
        if (!path) {
            setFolderPathError('Folder path is required');
            return false;
        }

        const invalidChars = /[<>"|?*]/;
        if (invalidChars.test(path)) {
            setFolderPathError('Path contains invalid characters (< > " | ? *)');
            return false;
        }

        if (!path.match(/^([a-zA-Z]:\\|\\\\|\/)/)) {
            setFolderPathError('Invalid path format. Use either:\n- Local path (C:\\Folder\\SubFolder)\n- Network path (\\\\Server\\Share\\Folder)\n- Unix path (/home/user/folder)');
            return false;
        }

        let maxLength;
        if (path.startsWith('\\\\')) maxLength = 32767;
        else if (path.startsWith('/')) maxLength = 4096;
        else maxLength = 260;

        if (path.length > maxLength) {
            setFolderPathError(`Path is too long. Maximum length is ${maxLength} characters`);
            return false;
        }

        setFolderPathError('');
        return true;
    };

    const validateFileFormat = (format) => {
        if (!format) {
            setFileFormatError('File format is required');
            return false;
        }

        const validFormatRegex = /^(\*\.[a-zA-Z0-9]+|\.[a-zA-Z0-9]+)$/;
        if (!validFormatRegex.test(format)) {
            setFileFormatError('Please enter a valid file format (e.g., .xlsx or *.pdf)');
            return false;
        }

        if (format.length > 10) {
            setFileFormatError('File format is too long');
            return false;
        }

        const commonFormats = ['.txt', '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.csv', '.json', '.xml'];
        if (!format.startsWith('*.') && !commonFormats.includes(format)) {
            setFileFormatError('Warning: Uncommon file format. Please verify if correct.');
            return false;
        }

        setFileFormatError('');
        return true;
    };

    // Update the process setting in fetchTriggerDetails
    // Add new loading state
    const [isLoading, setIsLoading] = useState(true);

    // Modify the fetchTriggerDetails function
    // In fetchTriggerDetails, update how we set the process
    const fetchTriggerDetails = useCallback(async () => {
        try {
            setIsLoading(true);
            const token = localStorage.getItem('userToken');
            const response = await axios.get(`${config.API_BASE_URL}/api/filePathTrigger/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            const trigger = response.data;
            console.log("🔍 Fetched trigger details:", trigger);
            console.log("📌 Initial stickto_robot value from DB:", trigger.stickto_robot);

            setFolderPath(trigger.folder_path || '');
            setFileFormat(trigger.file_format || '');
            setFolder(trigger.folder_id || '');
            setRobot(trigger.robot_id || 'All');
            setName(trigger.name || '');
            setStatus(trigger.status || '');
            setArgumentsList(trigger.argumentsList || []);
            setPriority(trigger.priority || 'Inherited');
            setStickToRobot(Boolean(trigger.stickto_robot));
            console.log("🔄 Converted stickto_robot to boolean:", Boolean(trigger.stickto_robot));
            
            // Update process state with all available process info
            if (trigger.process_id) {
                setProcess({
                    id: trigger.process_key,
                    name: trigger.process_name,
                    dbId: trigger.process_id
                });
            }
          
        } catch (error) {
            toast.error("Failed to load trigger details. Please try again.");
            console.error('Error fetching trigger details:', error);
        } finally {
            setIsLoading(false);
        }
    }, [id]);

    // Remove the incorrectly placed ProcessSelection component from here

    useEffect(() => {
        const fetchProcessName = async () => {
            const token = localStorage.getItem("userToken");
            if (!process?.dbId || !folder || !token) return;

            try {
                const response = await axios.get(`${config.API_BASE_URL}/api/orchestrator/releases`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "X-UIPATH-OrganizationUnitId": folder,
                    },
                });

                const releases = response.data?.releases;
                console.log("📡 Releases from Orchestrator:", releases);
                console.log("🔍 Looking for process with dbId:", process.dbId);

                const matched = releases.find(r => String(r.Id) === String(process.dbId));
                console.log("✅ Matched Release:", matched);

                if (matched) {
                    setProcess({
                        id: matched.Key,
                        name: matched.ProcessKey,
                        dbId: matched.Id
                    });
                }
            } catch (error) {
                // Only log if it's not a 401 error during initial load
                if (error?.response?.status !== 401) {
                    console.error("❌ Error fetching process name:", error);
                }
            }
        };

        fetchProcessName();
    }, [process?.dbId, folder]);

    // Modify handleSubmit function
    const handleSubmit = async () => {
        // Validate inputs before proceeding
        const isValidPath = validateFolderPath(folderPath);
        const isValidFormat = validateFileFormat(fileFormat);

        if (!isValidPath || !isValidFormat) {
            return;
        }

        try {
            const token = localStorage.getItem('userToken');
            const userId = localStorage.getItem('userId');

            if (!token || !userId) {
                toast.error("Authentication error. Please log in again.");
                return;
            }

            const payload = {
                name,
                status,
                filePathConfig: {
                    folderPath,
                    fileFormat,
                    process: process.id,
                    priority,
                    stickto_robot: stickToRobot ? 1 : 0  // Convert boolean to 0/1
                },
                robot_id: robot,
                argumentsList: argumentsList.filter(arg => arg.name && arg.value),
                folderId: folder,
                process_id: process.dbId
            };

            console.log("📤 Submitting payload with stickto_robot:", payload.filePathConfig.stickto_robot);
            console.log("🔍 Current stickToRobot state:", stickToRobot);
            console.log("📦 Full payload:", payload);

            const response = await axios.put(
                `${config.API_BASE_URL}/api/filePathTrigger/${id}`,
                payload,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            console.log("✅ Update response:", response.data);
            LoggingService.log(`User updated File Path Trigger: ID=${id}, Name="${name}"`);
            toast.success("File Path Trigger updated successfully!");
            navigate('/dashboard');
        } catch (error) {
            toast.error("Failed to update trigger. Please try again.");
            console.error('Error updating trigger:', error);
        }
    };

    // Add effect for initial data fetch
    useEffect(() => {
        fetchTriggerDetails();
    }, [fetchTriggerDetails]);

    // Remove this line
    // Move useEffect for process name

    return (
        <div className="trigger-container">
            <Helmet>
                <title>Edit File Path Trigger</title>
            </Helmet>
            <h2>Update File Path Trigger</h2>

            {!selectedTenant ? (
                <p className="error-message">Please select a tenant first.</p>
            ) : isLoading ? (
                <p>Loading trigger details...</p>
            ) : (
                <>
                    <div className="form-group">
                        <label>Trigger Name *</label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Enter Trigger Name"
                        />
                    </div>

                    <div className="form-group">
                        <label>Folder Path *</label>
                        <input
                            type="text"
                            value={folderPath}
                            onChange={(e) => {
                                setFolderPath(e.target.value);
                                validateFolderPath(e.target.value);
                            }}
                            placeholder="e.g., C:\Users\Documents or /home/user/documents"
                            className={folderPathError ? 'error-input' : ''}
                        />
                        {folderPathError && <div className="error-message">{folderPathError}</div>}
                    </div>

                    <div className="form-group">
                        <label>File Format *</label>
                        <input
                            type="text"
                            value={fileFormat}
                            onChange={(e) => {
                                setFileFormat(e.target.value);
                                validateFileFormat(e.target.value);
                            }}
                            placeholder=".xlsx, .pdf, .txt"
                            className={fileFormatError ? 'error-input' : ''}
                        />
                        {fileFormatError && <div className="error-message">{fileFormatError}</div>}
                    </div>
                    <div className="form-group">
                        <div style={{ display: 'flex', gap: 8, justifyContent: "space-between", alignItems: "center" }}>
                            <label>Input Arguments</label>
                            <button 
                                style={{ marginTop: 0 }} 
                                onClick={() => {
                                    const newArg = { name: `arg${argumentsList.length + 1}`, value: '' };
                                    setArgumentsList([...argumentsList, newArg]);
                                }}
                            >+</button>
                        </div>
                        {argumentsList?.length > 0 ? (
                            argumentsList.map((arg, index) => (
                                <div key={index} className="argument" style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                                    <input
                                        type="text"
                                        value={arg.name}
                                        onChange={(e) => {
                                            const newArgs = [...argumentsList];
                                            newArgs[index].name = e.target.value;
                                            setArgumentsList(newArgs);
                                        }}
                                        placeholder="Name"
                                    />
                                    <input
                                        type="text"
                                        value={arg.value}
                                        onChange={(e) => {
                                            const newArgs = [...argumentsList];
                                            newArgs[index].value = e.target.value;
                                            setArgumentsList(newArgs);
                                        }}
                                        placeholder="Value"
                                    />
                                    <button 
                                        style={{ marginTop: 0 }} 
                                        className='delete-button-small'
                                        onClick={() => {
                                            const newArgs = argumentsList.filter((_, i) => i !== index);
                                            setArgumentsList(newArgs);
                                        }}
                                    >Remove</button>
                                </div>
                            ))
                        ) : (
                            <p>No input arguments defined.</p>
                        )}
                    </div>
                    <FolderSelection folder={folder} onChange={setFolder} isGlobal={true} />
                    <RobotSelection robot={robot} onChange={setRobot} folder={folder} />
                    <div className="form-group process-robot-lock">
                        <div className="robot-lock-container">
                            <input
                                type="checkbox"
                                id="stickToRobot"
                                checked={stickToRobot}
                                onChange={handleStickToRobotChange}
                                disabled={robot === 'All'}
                            />
                            <label htmlFor="stickToRobot" className="robot-lock-label">
                                <span className="lock-icon">🔒</span>
                                Lock process to selected robot
                            </label>
                        </div>
                        {robot === 'All' ? (
                            <small className="help-text warning">
                                Select a specific robot to enable process locking
                            </small>
                        ) : (
                            <small className="help-text">
                                This process will only be executed by the selected robot
                            </small>
                        )}
                    </div>
                    <ProcessSelection
                        process={process}
                        onChange={(selected) => {
                            console.log("Process selected:", selected);
                            if (selected) {
                                const updatedProcess = {
                                    id: selected.Key || selected.id,
                                    name: selected.ProcessKey || selected.name,
                                    dbId: selected.Id  // Remove the fallback to process.dbId
                                };
                                console.log("Updated process state:", updatedProcess);
                                setProcess(updatedProcess);
                            } else {
                                setProcess({ id: "", name: "", dbId: "" });
                            }
                        }}
                        folder={folder}
                    />
                </>
            )}
            {!selectedTenant ? (
                <p className="error-message">Please select a tenant first.</p>
            ) : isLoading ? (
                <p>Loading trigger details...</p>
            ) : (
                <>
                    <div className="form-group">
                        <label>Job Priority</label>
                        <select 
                            value={priority} 
                            onChange={(e) => setPriority(e.target.value)}
                            className="form-control"
                        >
                            {priorities.map((p) => (
                                <option key={p.value} value={p.value}>
                                    {p.icon} {p.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        className="submit-button"
                    >
                        Update Trigger
                    </button>
                </>
            )}
        </div>
    );
};

export default EditFilePathTrigger;
